import type * as React from 'react';

import * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Button, DialogRoot, useDialogTriggerState } from '@gitbook/spine-react';

import { useUpgradeSite } from '../../../../state';
import { PaidFeatureIndicator } from '../../../Site';
import type { SiteOnlyCustomizationProps } from '../../types';
import { CreateCustomFontDialog } from '../Font/CreateCustomFontDialog';
import { ModifyCustomFontDialog } from '../Font/ModifyCustomFontDialog';
import type { CustomFontState } from './CustomFontState';

/**
 * Palette to pick a default or custom font
 */
export function EditCustomFontButton(
    props: SiteOnlyCustomizationProps & {
        state: CustomFontState;
    }
): React.ReactElement {
    const { state, site, organizationId } = props;
    const updateFontDialogTrigger = useDialogTriggerState();
    const upgradeCustomFonts = useUpgradeSite(site, api.SiteFeatureKey.SitesCustomFonts);

    return (
        <>
            {state.value ? (
                <Button
                    leadingIcon={Icon.Edit}
                    iconOnly
                    label={`Modify ${state.value.fontFamily}`}
                    kind="secondary"
                    onPress={updateFontDialogTrigger.open}
                />
            ) : (
                <div className="relative inline-block">
                    <Button
                        disabled={Boolean(upgradeCustomFonts)}
                        leadingIcon={Icon.AddNew}
                        iconOnly
                        label="Add custom font"
                        kind="secondary"
                        onPress={state.newFontDialogTrigger.open}
                    />

                    <div className="-translate-y-1/2 absolute top-0 right-0 translate-x-1/2 transform">
                        <PaidFeatureIndicator upgrade={upgradeCustomFonts} iconOnly size="xsmall" />
                    </div>
                </div>
            )}
            <DialogRoot {...state.newFontDialogTrigger.rootProps}>
                <CreateCustomFontDialog {...props} />
            </DialogRoot>
            <DialogRoot {...updateFontDialogTrigger.rootProps}>
                {state.value ? (
                    <ModifyCustomFontDialog
                        organizationId={organizationId}
                        fontId={state.value.id}
                        setSettings={props.setSettings}
                    />
                ) : null}
            </DialogRoot>
        </>
    );
}
